import type { IOfferChatsState } from 'shared/reducers/chat/chatReducer';
import type { IChatHintSchema } from 'shared/repositories/chats/entities/chat_hint/ChatHintSchema';

export enum EChatsActionTypes {
  SetRequestStatus = 'ISetRequestStatus',
  StateReceived = 'IChatsStateReceived',
  OfferChatReceived = 'IOfferChatReceived',
  OnlineStatusReceived = 'IOnlineStatusReceived',
  HintQuestionsReceived = 'IHintQuestionsReceived',
  OpenChat = 'IOpenChat',
  OpenChatbot = 'IOpenChatbot',
  CloseChat = 'ICloseChat',
  OpenChatAfterAuth = 'IOpenChatAfterAuth',
}

export type TChatAction =
  | ISetRequestStatus
  | IChatsStateReceived
  | IOfferChatReceived
  | IOnlineStatusReceived
  | IHintQuestionsReceived
  | IOpenChat
  | IOpenChatbot
  | ICloseChat
  | IOpenChatAfterAuth;

export interface ISetRequestStatus {
  type: EChatsActionTypes.SetRequestStatus;
  payload: {
    status: IOfferChatsState['requestStatus'];
  };
}

export const setRequestStatus = (status: IOfferChatsState['requestStatus']): ISetRequestStatus => ({
  payload: { status },
  type: EChatsActionTypes.SetRequestStatus,
});

export interface IChatsStateReceived {
  type: EChatsActionTypes.StateReceived;
  payload: {
    enabled: boolean;
  };
}

export const chatsStateReceived = (enabled: boolean): IChatsStateReceived => ({
  payload: { enabled },
  type: EChatsActionTypes.StateReceived,
});

export interface IOfferChatReceived {
  type: EChatsActionTypes.OfferChatReceived;
  payload: {
    chatId: string;
  };
}

export const offerChatReceived = (chatId: string): IOfferChatReceived => ({
  payload: { chatId },
  type: EChatsActionTypes.OfferChatReceived,
});

export interface IOnlineStatusReceived {
  type: EChatsActionTypes.OnlineStatusReceived;
  payload: {
    isOnline: boolean;
  };
}

export const onlineStatusReceived = (isOnline: boolean): IOnlineStatusReceived => ({
  payload: { isOnline },
  type: EChatsActionTypes.OnlineStatusReceived,
});

export interface IHintQuestionsReceived {
  type: EChatsActionTypes.HintQuestionsReceived;
  payload: {
    hintQuestions: IChatHintSchema[];
  };
}

export const hintQuestionsReceived = (hintQuestions: IChatHintSchema[]): IHintQuestionsReceived => ({
  payload: { hintQuestions },
  type: EChatsActionTypes.HintQuestionsReceived,
});

export type TOpenChatParameters = {
  type?: 'open_phone' | 'open_message';
  defaultMessage?: string;
  source?: string;
  messagePayload?: string;
  shouldSendDefaultMessage?: boolean;
  placeType?: string;
  isEarlyAccess?: boolean;
};

export interface IOpenChat {
  type: EChatsActionTypes.OpenChat;
  payload: TOpenChatParameters;
}

export const openChat = (parameters: TOpenChatParameters | void): IOpenChat => ({
  type: EChatsActionTypes.OpenChat,
  payload: parameters || {},
});

export type TOpenChatbotParameters = {
  defaultMessage?: string;
};

export interface IOpenChatbot {
  type: EChatsActionTypes.OpenChatbot;
  payload: TOpenChatbotParameters;
}

export const openChatbot = (parameters: TOpenChatbotParameters = {}): IOpenChatbot => ({
  type: EChatsActionTypes.OpenChatbot,
  payload: parameters,
});

export interface ICloseChat {
  type: EChatsActionTypes.CloseChat;
}

export const closeChat = (): ICloseChat => ({
  type: EChatsActionTypes.CloseChat,
});

export interface IOpenChatAfterAuth {
  type: EChatsActionTypes.OpenChatAfterAuth;
  payload: TOpenChatParameters;
}

export const openChatAfterAuth = (parameters: TOpenChatParameters | void): IOpenChatAfterAuth => ({
  type: EChatsActionTypes.OpenChatAfterAuth,
  payload: parameters || {},
});
